import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/MarketplaceCampaignReport.graphql';
import { Query, QuerymarketplaceCampaignReportArgs } from '../../__generated__/globalTypes';

export const useMarketplaceCampaignReportQuery = (
  options?: QueryHookOptions<Query, QuerymarketplaceCampaignReportArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerymarketplaceCampaignReportArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useMarketplaceCampaignReportLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerymarketplaceCampaignReportArgs>
): QueryResult<Query, QuerymarketplaceCampaignReportArgs> & {
  getMarketplaceCampaignReport: LazyQueryExecFunction<Query, QuerymarketplaceCampaignReportArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getMarketplaceCampaignReport, result] = useLazyQuery<Query, QuerymarketplaceCampaignReportArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getMarketplaceCampaignReport, ...result };
};

export const useMarketplaceCampaignReportPromiseQuery = () => {
  const client = useApolloClient();

  const getMarketplaceCampaignReport = useCallback(
    (options?: Omit<QueryOptions<QuerymarketplaceCampaignReportArgs, Query>, 'query'>) =>
      client.query<Query, QuerymarketplaceCampaignReportArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getMarketplaceCampaignReport };
};
// Suspense Query Hooks
export const useMarketplaceCampaignReportSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerymarketplaceCampaignReportArgs>
) => {
  const result = useSuspenseQuery<Query, QuerymarketplaceCampaignReportArgs>(QUERY, {
    ...options
  });

  return result;
};
